import { useEffect, useState } from "react";
import { toast } from "sonner";
import { clearCurrentUserPin, setCurrentUserPin } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { clearAuthSession, getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PIN_MIN_LENGTH, PinInput } from "@/components/auth/pin-input";

/**
 * Two-stage PIN setup for the CURRENT session's user: choose → confirm, always skippable.
 * Setting a PIN requires password re-auth server-side; the login flows thread the just-typed
 * password in as a prop (memory only, dropped when the dialog closes) so first-login setup
 * costs zero extra typing. Opened standalone (profile menu "Set/Change PIN") it shows its own
 * password field instead.
 */
export function PinSetupDialog({ open, onOpenChange, password, onDone }) {
  const hasPasswordProp = password != null;
  const [stage, setStage] = useState("choose");
  const [chosenPin, setChosenPin] = useState("");
  const [pinDraft, setPinDraft] = useState("");
  const [ownPassword, setOwnPassword] = useState("");
  const [errorText, setErrorText] = useState("");
  const [shakeSignal, setShakeSignal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fresh start every time the dialog opens (and drop the standalone password on close).
  useEffect(() => {
    setStage("choose");
    setChosenPin("");
    setPinDraft("");
    setOwnPassword("");
    setErrorText("");
    setIsSubmitting(false);
  }, [open]);

  const close = (outcome) => {
    onOpenChange?.(false);
    onDone?.(outcome);
  };

  const handleChooseSubmit = (pin) => {
    if (pin.length < PIN_MIN_LENGTH) return;
    if (!hasPasswordProp && !ownPassword.trim()) {
      setErrorText("Enter your password to confirm it's you.");
      return;
    }
    setChosenPin(pin);
    setPinDraft("");
    setErrorText("");
    setStage("confirm");
  };

  // A 401 from /api/users/me/pin is ambiguous: "unauthorized" means the SESSION token died
  // (the endpoint is excluded from the global auth-expired bridge precisely because its 401s
  // usually mean wrong password) — treat that as expiry, or the user would retype a correct
  // password forever; any other 401 body is a genuinely wrong password.
  const handleSessionExpired = () => {
    close("expired");
    clearAuthSession();
    toast.error("Your session has expired. Please sign in again.");
  };

  const handleConfirmSubmit = async (pin) => {
    if (isSubmitting) return;
    if (pin !== chosenPin) {
      setPinDraft("");
      setErrorText("PINs don't match — try again.");
      setShakeSignal((n) => n + 1);
      return;
    }
    setIsSubmitting(true);
    try {
      await setCurrentUserPin({
        pin,
        password: hasPasswordProp ? password : ownPassword,
      });
      toast.success("PIN set. Use it to switch to this profile.");
      close("set");
    } catch (error) {
      console.error("[Auth] PIN setup failed:", error);
      setPinDraft("");
      setShakeSignal((n) => n + 1);
      if (error instanceof ApiError && error.status === 401) {
        if (String(error.payload?.error || "") === "unauthorized") {
          handleSessionExpired();
          return;
        }
        setErrorText("Password verification failed. Check the password and try again.");
        if (!hasPasswordProp) setStage("choose");
      } else if (error instanceof ApiError) {
        // Old backend without the PIN endpoints (404) or a policy rejection — degrade quietly.
        setErrorText("PIN setup is unavailable right now. You can keep signing in with your password.");
      } else {
        setErrorText("Could not connect to the server. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Standalone "Change PIN" flow only: drop the PIN entirely (password re-auth required).
  const handleRemovePin = async () => {
    if (isSubmitting) return;
    if (!ownPassword.trim()) {
      setErrorText("Enter your password to confirm it's you.");
      return;
    }
    setIsSubmitting(true);
    try {
      await clearCurrentUserPin({ password: ownPassword });
      toast.success("PIN removed. Use your password to switch to this profile.");
      close("removed");
    } catch (error) {
      console.error("[Auth] PIN removal failed:", error);
      if (error instanceof ApiError && error.status === 401) {
        if (String(error.payload?.error || "") === "unauthorized") {
          handleSessionExpired();
          return;
        }
        setErrorText("Password verification failed. Check the password and try again.");
      } else {
        setErrorText("Could not remove the PIN. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isChooseStage = stage === "choose";
  // Standalone (opened from the profile menu, own password field) vs the first-login prompt.
  // With an existing PIN the standalone flow reads as "change" and offers removal.
  const isChangeFlow = !hasPasswordProp && Boolean(getAuthUser()?.hasPin);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange?.(true) : close("skipped"))}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {!isChooseStage
              ? "Confirm your PIN"
              : isChangeFlow
                ? "Change your quick-switch PIN"
                : "Set a quick-switch PIN"}
          </DialogTitle>
          <DialogDescription>
            {isChooseStage
              ? "Choose a 4-6 digit PIN to switch to this profile quickly on this device."
              : "Enter the same PIN once more."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          {isChooseStage && !hasPasswordProp ? (
            <Input
              type="password"
              value={ownPassword}
              onChange={(event) => setOwnPassword(event.target.value)}
              placeholder="Your password"
              disabled={isSubmitting}
              className="h-10"
            />
          ) : null}
          <PinInput
            key={stage}
            value={pinDraft}
            onChange={(next) => {
              setPinDraft(next);
              if (errorText) setErrorText("");
            }}
            onSubmit={isChooseStage ? handleChooseSubmit : (pin) => void handleConfirmSubmit(pin)}
            disabled={isSubmitting}
            autoFocus={hasPasswordProp || !isChooseStage}
            error={Boolean(errorText)}
            shakeSignal={shakeSignal}
            ariaLabel={isChooseStage ? "New PIN" : "Confirm PIN"}
          />
          {errorText ? <p className="text-sm text-destructive">{errorText}</p> : null}
        </div>
        <DialogFooter>
          {isChangeFlow && isChooseStage ? (
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={() => void handleRemovePin()}
              disabled={isSubmitting}
            >
              Remove PIN
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => close("skipped")} disabled={isSubmitting}>
            {isChangeFlow ? "Cancel" : "Skip"}
          </Button>
          {isChooseStage ? (
            <Button
              type="button"
              onClick={() => handleChooseSubmit(pinDraft)}
              disabled={isSubmitting || pinDraft.length < PIN_MIN_LENGTH}
            >
              Continue
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void handleConfirmSubmit(pinDraft)}
              disabled={isSubmitting || pinDraft.length < PIN_MIN_LENGTH}
            >
              {isSubmitting ? "Setting…" : "Set PIN"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// App-level first-login prompt. The page tree is remounted (key={activeUserId}) the moment a
// login lands, which would destroy any dialog owned by the login form itself — so the login
// flows *request* the prompt here (module state, memory only) and App mounts <PinSetupPrompt/>
// outside the keyed subtree to render it.

let pendingPinSetup = null; // { user, password } — never persisted
const PIN_SETUP_REQUEST_EVENT = "healo-pin-setup-request";

/**
 * Call right after a successful credentials login: prompts PIN setup when the backend says the
 * (staff) user has none. `hasPin !== false` covers old backends that omit the field — no prompt.
 */
export function maybePromptPinSetupAfterLogin(result, password) {
  const user = result?.user;
  if (!user || !user.isEmployee || user.hasPin !== false) return;
  pendingPinSetup = { user, password: String(password || "") };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(PIN_SETUP_REQUEST_EVENT));
  }
}

export function PinSetupPrompt() {
  const [request, setRequest] = useState(() => pendingPinSetup);

  useEffect(() => {
    const handler = () => setRequest(pendingPinSetup);
    window.addEventListener(PIN_SETUP_REQUEST_EVENT, handler);
    return () => window.removeEventListener(PIN_SETUP_REQUEST_EVENT, handler);
  }, []);

  // setCurrentUserPin acts on the ACTIVE session — if the identity moves (another switch,
  // logout, expiry) before the prompt is answered, drop it rather than set someone else's PIN.
  useEffect(
    () =>
      subscribeAuthStateChange(() => {
        if (pendingPinSetup && String(getAuthUser()?.id || "") !== pendingPinSetup.user.id) {
          pendingPinSetup = null;
          setRequest(null);
        }
      }),
    [],
  );

  if (!request) return null;

  return (
    <PinSetupDialog
      key={String(request.user.id || "")}
      open
      password={request.password}
      onOpenChange={(open) => {
        if (!open) {
          pendingPinSetup = null;
          setRequest(null);
        }
      }}
    />
  );
}
